/**
 * Global safety utilities for crash prevention
 * Safari-first stability with graceful recovery
 */

// Development vs production detection
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Global invariant helper - logs errors in dev, recovers silently in prod
 */
export function invariant(condition: boolean, message: string, recoveryValue?: any): asserts condition {
  if (!condition) {
    if (isDevelopment) {
      console.error('🚨 INVARIANT VIOLATION:', message);
      console.trace();
    } else {
      console.warn('Safety check failed:', message);
    }
    
    // In production, we never throw - we recover
    if (!isDevelopment) {
      return recoveryValue;
    }
    
    throw new Error(`Invariant violation: ${message}`);
  }
}

/**
 * Safe array access with bounds checking
 */
export function safeArrayAccess<T>(array: T[] | undefined, index: number): T | null {
  try {
    if (!array || !Array.isArray(array)) {
      invariant(false, 'safeArrayAccess: array is not an array', null);
      return null;
    }
    
    if (index < 0 || index >= array.length) {
      invariant(false, `safeArrayAccess: index ${index} out of bounds [0, ${array.length - 1}]`, null);
      return null;
    }
    
    return array[index];
  } catch (error) {
    console.error('safeArrayAccess failed:', error);
    return null;
  }
}

/**
 * Clamp index to valid range
 */
export function clampIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return Math.max(0, Math.min(index, length - 1));
}

/**
 * Safe question access with validation
 */
export interface SafeQuestion {
  id: string;
  question: string;
  text: string;
  options: { id: string; text: string }[];
  answerId: string;
  correctAnswerId: string;
  explanation: string;
  imageUrl?: string;
  imageHint?: string;
  subject: string;
  topic: string;
  cbrReference?: string;
  legalBasis?: string;
  detailedExplanation?: string;
  whyWrong?: { [optionId: string]: string };
}

export function safeQuestionAccess(questions: SafeQuestion[] | undefined, index: number): SafeQuestion | null {
  const question = safeArrayAccess(questions, index);
  
  if (!question) {
    invariant(false, `safeQuestionAccess: no question at index ${index}`, null);
    return null;
  }
  
  // Validate required fields
  if (!question.id) {
    invariant(false, `safeQuestionAccess: question missing id at index ${index}`, null);
    return null;
  }
  
  if (!question.text) {
    invariant(false, `safeQuestionAccess: question missing text at index ${index}`, null);
    return null;
  }
  
  if (!Array.isArray(question.options)) {
    invariant(false, `safeQuestionAccess: question missing options at index ${index}`, null);
    return null;
  }
  
  if (!question.correctAnswerId) {
    invariant(false, `safeQuestionAccess: question missing correctAnswerId at index ${index}`, null);
    return null;
  }
  
  if (!question.explanation) {
    invariant(false, `safeQuestionAccess: question missing explanation at index ${index}`, null);
    return null;
  }
  
  if (!question.subject) {
    invariant(false, `safeQuestionAccess: question missing subject at index ${index}`, null);
    return null;
  }
  
  return question;
}

/**
 * Safe browser API wrapper with try/catch
 */
export function safeBrowserAPI<T>(
  apiCall: () => T,
  fallback: T,
  context: string
): T {
  try {
    const result = apiCall();
    return result !== undefined ? result : fallback;
  } catch (error) {
    console.error(`Browser API failed [${context}]:`, error);
    return fallback;
  }
}

/**
 * Safe timer utilities
 */
export const safeTimers = {
  setTimeout: (callback: () => void, delay: number, context: string) => {
    return safeBrowserAPI(
      () => window.setTimeout(() => {
        try {
          callback();
        } catch (error) {
          console.error(`Timer callback failed [${context}]:`, error);
        }
      }, delay),
      null,
      context
    );
  },
  
  clearTimeout: (id: number | null) => {
    if (id !== null) {
      safeBrowserAPI(() => window.clearTimeout(id), undefined, 'clearTimeout');
    }
  },
  
  requestAnimationFrame: (callback: () => void, context: string) => {
    return safeBrowserAPI(
      () => window.requestAnimationFrame(() => {
        try {
          callback();
        } catch (error) {
          console.error(`RAF callback failed [${context}]:`, error);
        }
      }),
      null,
      context
    );
  },
  
  cancelAnimationFrame: (id: number | null) => {
    if (id !== null) {
      safeBrowserAPI(() => window.cancelAnimationFrame(id), undefined, 'cancelAnimationFrame');
    }
  }
};


/**
 * Visibility change handler for Safari
 */
export function setupVisibilityChangeHandler(onHidden: () => void) {
  return safeBrowserAPI(
    () => {
      const handleVisibilityChange = () => {
        if (document.hidden) {
          onHidden();
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Return cleanup function
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    },
    () => () => {}, // Return noop cleanup
    'setupVisibilityChangeHandler'
  );
}

/**
 * Global error boundary for unhandled errors
 */
export function setupGlobalErrorBoundary() {
  if (isDevelopment) return;
  
  window.addEventListener('error', (event) => {
    console.error('Global error caught:', event.error);
    // In production, we prevent the error from bubbling up
    event.preventDefault();
  });
  
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    // In production, we prevent the rejection from bubbling up
    event.preventDefault();
  });
}

// Initialize global safety
if (typeof window !== 'undefined') {
  setupGlobalErrorBoundary();
}
